import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTemplates } from '../../context/TemplatesContext';
import { useAuth } from '../../context/AuthContext';
import './Header.css';
import { FaUsers, FaCaretDown, FaUserCircle, FaSignOutAlt } from 'react-icons/fa';

// CARQ-75：LOGO 是否有效＝必須是 data: 開頭的 base64 字串。空字串、null、舊資料中殘留
// 的非 data: 字串（例如曾被存成檔名）都視為「沒有 LOGO」，改走「公司名稱」文字 fallback。
const isValidLogo = (src) => typeof src === 'string' && src.startsWith('data:');

const Header = () => {
  const { templates, loading: templatesLoading } = useTemplates();
  const { currentUser, logout } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const rawLogo = templates?.companyLogoBase64;
  const logoSrc = isValidLogo(rawLogo) ? rawLogo : null;
  const companyName = templates?.companyName || currentUser?.companyName || '公司';

  // CARQ-75 螢幕診斷：在網址加 ?carq75debug=1 時顯示 LOGO 載入狀態，方便 Bridget
  // 不開 DevTools 就能判斷「沒 LOGO」是「DB 沒值」、「還沒載完」還是「值格式錯」。
  const debugEnabled = typeof window !== 'undefined'
    && window.location?.search?.includes('carq75debug=1');

  const isSuperAdmin = currentUser && currentUser.role === 'superadmin';
  const isAdmin = currentUser && currentUser.role === 'admin';
  const canManageUsers = isSuperAdmin || isAdmin;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    setIsDropdownOpen(false);
    logout();
  };

  return (
    <header className="header">
      <div className="header__inner">
        <div className="header__logo">
          <Link to="/">
            {logoSrc ? (
              <img src={logoSrc} alt={`${companyName} LOGO`} />
            ) : templatesLoading ? (
              // 載入中：保持原本不可見的尺寸佔位，避免 layout shift
              <span className="header__logo-placeholder" aria-hidden="true" />
            ) : (
              // CARQ-75 fallback：templates 載完但 companyLogoBase64 是空 / 格式錯時，
              // 用公司名稱當文字版 LOGO，讓左上角永遠不會看起來「空空的」。
              <span className="header__logo-text" aria-label={`${companyName} LOGO`}>
                {companyName}
              </span>
            )}
          </Link>
        </div>

        {debugEnabled && (
          <div className="header__logo-debug" role="status">
            <strong>CARQ-75 LOGO debug</strong>
            <div>templatesLoading: {String(templatesLoading)}</div>
            <div>currentUser: {currentUser ? currentUser.email || currentUser._id || 'yes' : 'null'}</div>
            <div>templates.companyName: {templates?.companyName || '(empty)'}</div>
            <div>companyLogoBase64 type: {rawLogo == null ? 'null/undefined' : typeof rawLogo}</div>
            <div>companyLogoBase64 length: {typeof rawLogo === 'string' ? rawLogo.length : 'n/a'}</div>
            <div>starts with data: {typeof rawLogo === 'string' ? String(rawLogo.startsWith('data:')) : 'n/a'}</div>
            <div>rendered: {logoSrc ? 'img' : templatesLoading ? 'placeholder' : 'text-fallback'}</div>
          </div>
        )}

        {currentUser && (
          <div className="header__user" ref={dropdownRef}>
            <div className="header__user-toggle" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
              <span>{companyName} - {currentUser.name}</span>
              <FaCaretDown className={isDropdownOpen ? 'rotate' : ''} />
            </div>
            {isDropdownOpen && (
              <ul className="header__dropdown">
                <li>
                  <Link to="/profile" onClick={() => setIsDropdownOpen(false)}>
                    <FaUserCircle /> 個人資料
                  </Link>
                </li>
                {false && canManageUsers && (
                  <li>
                    <Link
                      to={isSuperAdmin ? "/users" : `/companies/${currentUser.company?._id || currentUser.company}/users`}
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <FaUsers /> 用戶管理
                    </Link>
                  </li>
                )}
                <li>
                  <button onClick={handleLogout}>
                    <FaSignOutAlt /> 登出
                  </button>
                </li>
              </ul>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
